import { React, useContext, useState, useEffect } from 'react';
import { useTheme } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { AgendaContext } from '../contexts/AgendaContext';
// Mui components
import {
  Box,
  Stepper,
  Step,
  StepIcon,
  StepLabel,
  Tooltip,
  Typography
} from '@mui/material';
// Components
import FooterSolicitudDeConexion from '../components/SolicitudDeConexion/FooterSolicitudDeConexion';
import PantallaProveedor from '../components/SolicitudDeConexion/Proveedor/PantallaProveedor';
import PantallaSeleccionConsultorio from '../components/SolicitudDeConexion/Consultorios/PantallaSeleccionConsultorio';
import PantallaSeleccionEspecialidades from '../components/SolicitudDeConexion/Especialidades/PantallaSeleccionEspecialidades';
import PantallaFechaPublicacion from '../components/SolicitudDeConexion/FechaPublicacion/PantallaFechaPublicacion';
import PantallaVerificacionDatos from '../components/SolicitudDeConexion/VerificacionDatos/PantallaVerificacionDatos';
import RouterBlocker from '../components/common/RouterBlocker';
// Modals
import ModalConfirmacionEnvio from '../components/SolicitudDeConexion/Modals/ModalConfirmacionEnvio.flujoSolicitud';
import ModalInformarProveedor from '../components/SolicitudDeConexion/Modals/ModalInformarProveedor.flujoSolicitud';
import ModalErrorExterno from '../components/SolicitudDeConexion/Modals/ModalErrorExterno.flujoSolicitud';
import ModalErrorPost from '../components/SolicitudDeConexion/Modals/ModalErrorPost.flujoSolicitud';
import ModalSuccessPost from '../components/SolicitudDeConexion/Modals/ModalSuccessPost.flujoSolicitud';
// Constants
import { modales } from '../utils/Constants/Modales';
import { steps } from '../utils/Constants/Steps';
// Icons
import iconClose from '../assets/icons/icon_close.svg';
// ErrorProvider
import { useError } from '../contexts/ErrorProvider';
// Modals
import ModalErrorExternoConectados from '../components/SolicitudDeConexion/Modals/ModalErrorExternoConectados.flujoSolicitud';
// Hooks
import { datosContactoFormatted } from '../utils/Hooks/datosContactoFormatted';
import { CONSULTORIO_CONNECTION_BLOCKING_REASON } from '../utils/Constants/consultorioConnectionBlocking';
//Skeletons
import SolicitudDeConexionSkeleton from '../components/common/SolicitudDeConexionSkeleton.jsx';

export default function SolicitudDeConexion() {
  const theme = useTheme();
  const navigate = useNavigate();

  const {
    isLoading,
    solicitudProps,
    setSolicitudProps,
    getExterno,
    proveedorSeleccionado,
    setUseBlockerOK,
    getSolicitudesWithContrato,
    getConsultoriosWithContrato,
    setContratoSeleccionado,
    getDatosContactoPrestador,
    datosContactoPrestadorLogeado,
    prestadoresOsde,
    consultoriosContrato
  } = useContext(AgendaContext);

  const { handleError } = useError();

  useEffect(() => {
    if (isLoading == true) {
      getInfoSolicitudDeConexionComponentMount();
    }
  }, [isLoading]);

  useEffect(() => {
    if (datosContactoPrestadorLogeado && solicitudProps.datosContacto.length === 0) {
      const datosPrestador = datosContactoFormatted(datosContactoPrestadorLogeado);

      setSolicitudProps({
        ...solicitudProps,
        datosContacto: datosPrestador.datosPrestador,
        filial: prestadoresOsde?.consultorioOsde[0]?.filial
      });
    }
  }, []);

  const getInfoSolicitudDeConexionComponentMount = async () => {
    const storedContrato = localStorage.getItem('contratoSeleccionado');
    if (!storedContrato) {
      handleError({ error: 'contratoNull', description: 'initializeError' });
    } else {
      try {
        const consultorioData = await getConsultoriosWithContrato(storedContrato);
        if (
          consultorioData?.consultorioDigital?.blockingReason ===
          CONSULTORIO_CONNECTION_BLOCKING_REASON.NO_PUBLISHED_IN_OSDE_CARTILLA
        ) {
          navigate('/');
          return;
        }
        await getSolicitudesWithContrato(storedContrato);
        await setContratoSeleccionado(storedContrato);
        await getDatosContactoPrestador(storedContrato);
      } catch (error) {
        handleError({ error: 'contratoNull', description: 'initializeError' });
      }
    }
  };

  const [loader, setLoader] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [errorInFechaPublicacion, setErrorInFechaPublicacion] = useState(null);
  const [openModals, setOpenModals] = useState(modales);
  const [stepHandler, setStepHandler] = useState(null);
  const { cuit } = consultoriosContrato

  const handleClose = () => setOpenModals(modales);

  const clearForm = () => {
    setSolicitudProps({ datosContacto: [] });
  };

  useEffect(() => {
    setUseBlockerOK(true);
  }, []);

  const postInformation = async () => {
    switch (activeStep) {
      case 0:
        if (proveedorSeleccionado !== null) {
          try {
            const externoResponse = await getExterno(cuit, proveedorSeleccionado.nombre);
            const verifExternoDisponible = !externoResponse.data.consultorioExterno.some(
              (elem) =>
                elem.estadoEnInteoperabilidad === 'BAJA' || elem.estadoEnInteoperabilidad === null
            );
            if (verifExternoDisponible) {
              setOpenModals({ ...modales, errorExternoConectados: true });
              return false;
            }
            return true;
          } catch (error) {
            setOpenModals({ ...modales, errorExterno: true });
            console.error(error);
          }
        }
        return false;
      case 1:
      case 2:
      case 3:
        return true;
    }
  };

  const handleNext = async () => {
    if (activeStep !== 4) {
      try {
        setLoader(true);
        const resultadoPost = await postInformation(activeStep, setOpenModals);
        if (resultadoPost === true) {
          setActiveStep((nextActiveStep) => nextActiveStep + 1);
        }
        setLoader(false);
      } catch (error) {
        setOpenModals({ ...modales, errorExterno: true });
        console.error(error);
        setLoader(false);
      }
    }
    if (activeStep === 4) {
      setOpenModals({ ...modales, confirmacionEnvio: true });
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate('/prestador/dashboard');
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const getStepContent = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return (
          <>
            <PantallaProveedor modales={modales} setOpenModals={setOpenModals} />
            <ModalInformarProveedor
              openModal={openModals.informarProveedor}
              onHandleClose={handleClose}
            />
            <ModalErrorExterno openModal={openModals.errorExterno} onHandleClose={handleClose} />
            <ModalErrorExternoConectados
              openModal={openModals.errorExternoConectados}
              onHandleClose={handleClose}
            />
          </>
        );

      case 1:
        return <PantallaSeleccionConsultorio />;

      case 2:
        return <PantallaSeleccionEspecialidades />;

      case 3:
        return (
          <PantallaFechaPublicacion
            errorInFechaPublicacion={errorInFechaPublicacion}
            setErrorInFechaPublicacion={setErrorInFechaPublicacion}
          />
        );

      case 4:
        return (
          <>
            <PantallaVerificacionDatos
              setActiveStep={setActiveStep}
              setStepHandler={setStepHandler}
            />
            <ModalConfirmacionEnvio
              openModals={openModals.confirmacionEnvio}
              onHandleClose={handleClose}
              modales={modales}
              setOpenModals={setOpenModals}
            />
            <ModalErrorPost
              openModals={openModals.errorPost}
              onHandleClose={handleClose}
              clearForm={clearForm}
            />
            <ModalSuccessPost
              openModals={openModals.successPost}
              onHandleClose={handleClose}
              clearForm={clearForm}
            />
          </>
        );

      default:
        return 'paso no encontrado';
    }
  };

  return (
    <>
      {isLoading ? (
        <SolicitudDeConexionSkeleton />
      ) : (
        <RouterBlocker
          clearForm={clearForm}
          blockCondition={solicitudProps}
          currentComponent="registro">
          <Box sx={{ width: '100%', padding: '2em 4em 0' }}>
            <Box
              sx={{
                display: 'flex',
                gap: '8em',
                minHeight: 'calc(100vh - 76px - 9em)',
                marginTop: '76px'
              }}>
              <Box sx={{ width: '25%', marginTop: '40px' }}>
                <Stepper activeStep={activeStep} orientation="vertical">
                  {steps.map((step) => (
                    <Step key={step.id}>
                      <StepLabel StepIconComponent={CustomStepIcon}>
                        <Typography
                          variant="h5"
                          component="span"
                          sx={{
                            ml: 0.5,
                            color:
                              step.id === activeStep
                                ? theme.palette.primary.main
                                : theme.palette.grayMedium.main
                          }}>
                          {step.label}
                        </Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginRight: '3em',
                  width: '100%'
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    height: '40px',
                    paddingRight: '1em',
                    boxSizing: 'content-box'
                  }}>
                  <Tooltip title="Cancelar solicitud." placement="top-start">
                    <Box
                      component="img"
                      sx={{
                        width: '24px',
                        height: '24px',
                        cursor: 'pointer',
                        borderRadius: '100%',
                        padding: '0.2em',
                        '&:hover': {
                          backgroundColor: '#E7E9F7'
                        }
                      }}
                      alt="img not found"
                      src={iconClose}
                      onClick={() => navigate('/prestador/dashboard')}
                    />
                  </Tooltip>
                </Box>

                <Box>{getStepContent(activeStep)}</Box>
              </Box>
            </Box>
            <FooterSolicitudDeConexion
              errorInFechaPublicacion={errorInFechaPublicacion}
              onHandleBack={handleBack}
              onHandleNext={handleNext}
              activeStep={activeStep}
              stepHandler={stepHandler}
              setStepHandler={setStepHandler}
              setActiveStep={setActiveStep}
              loader={loader}
            />
          </Box>
        </RouterBlocker>
      )}
    </>
  );
}

function CustomStepIcon(props) {
  const { completed } = props;

  if (completed) {
    return (
      <StepIcon
        icon={
          <Box
            sx={{
              color: '#1226AA',
              border: '2px solid #1226AA',
              backgroundColor: '#E7E9F7',
              fontWeight: 800,
              borderRadius: '100%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <p style={{ fontSize: '10px' }}>{props.icon}</p>
          </Box>
        }
      />
    );
  }

  return <StepIcon {...props} />;
}
